import { useState, useEffect, useCallback, useRef } from 'react';
import DetectionService from './DetectionService';
import { DETECTION_MODELS } from './config';

export function useFootDetection(options = {}) {
  const {
    modelType = DETECTION_MODELS.COCO_SSD,
    autoStart = false,
    onDetection = null,
    onError = null
  } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [manualBoxes, setManualBoxes] = useState(null);

  const animationFrameRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const isRunningRef = useRef(false);

  const initialize = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      await DetectionService.initialize(modelType);
      setIsReady(true);
    } catch (err) {
      setError(err.message);
      if (onError) onError(err);
    } finally {
      setIsLoading(false);
    }
  }, [modelType, onError]);

  const runDetection = useCallback(async () => {
    if (!isRunningRef.current || !videoRef.current) return;

    try {
      let detectionResults = await DetectionService.detect(videoRef.current);

      if (manualBoxes && detectionResults) {
        detectionResults = {
          ...detectionResults,
          feet: detectionResults.feet.map(foot => {
            const manual = manualBoxes[foot.side];
            if (manual) {
              return {
                ...foot,
                boundingBox: manual,
                isManual: true
              };
            }
            return foot;
          })
        };
      }

      if (detectionResults) {
        setResults(detectionResults);

        if (onDetection) {
          onDetection(detectionResults);
        }

        if (canvasRef.current) {
          const ctx = canvasRef.current.getContext('2d');
          canvasRef.current.width = videoRef.current.videoWidth;
          canvasRef.current.height = videoRef.current.videoHeight;
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          DetectionService.drawDetections(ctx, detectionResults);
        }
      }
    } catch (err) {
      console.error('Detection error:', err);
    }

    if (isRunningRef.current) {
      animationFrameRef.current = requestAnimationFrame(runDetection);
    }
  }, [onDetection, manualBoxes]);

  const start = useCallback(async (video, canvas = null) => {
    if (!DetectionService.isInitialized) {
      await initialize();
    }

    videoRef.current = video;
    canvasRef.current = canvas;
    isRunningRef.current = true;
    setIsRunning(true);

    runDetection();
  }, [initialize, runDetection]);

  const stop = useCallback(() => {
    isRunningRef.current = false;
    setIsRunning(false);

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }

    setResults(null);
  }, []);

  const detectOnce = useCallback(async (video) => {
    if (!DetectionService.isInitialized) {
      await initialize();
    }
    return await DetectionService.detect(video);
  }, [initialize]);

  const drawResults = useCallback((ctx, detectionResults, options) => {
    DetectionService.drawDetections(ctx, detectionResults, options);
  }, []);

  const updateConfig = useCallback((newConfig) => {
    DetectionService.updateConfig(newConfig);
  }, []);

  const setManualBox = useCallback((side, box) => {
    setManualBoxes(prev => ({
      ...prev,
      [side]: box
    }));
  }, []);

  const clearManualBoxes = useCallback(() => {
    setManualBoxes(null);
  }, []);

  const adjustBoxSize = useCallback((side, widthDelta, heightDelta) => {
    if (!results) return;

    const foot = results.feet.find(f => f.side === side);
    if (!foot) return;

    const newBox = {
      ...foot.boundingBox,
      width: Math.max(20, foot.boundingBox.width + widthDelta),
      height: Math.max(20, foot.boundingBox.height + heightDelta)
    };

    setManualBox(side, newBox);
  }, [results, setManualBox]);

  const moveBox = useCallback((side, xDelta, yDelta) => {
    if (!results) return;

    const foot = results.feet.find(f => f.side === side);
    if (!foot) return;

    const newBox = {
      ...foot.boundingBox,
      x: foot.boundingBox.x + xDelta,
      y: foot.boundingBox.y + yDelta
    };

    setManualBox(side, newBox);
  }, [results, setManualBox]);

  useEffect(() => {
    if (autoStart) {
      initialize();
    }
  }, [autoStart, initialize]);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  useEffect(() => {
    const unsubscribe = DetectionService.subscribe((event, data) => {
      switch (event) {
        case 'loading':
          setIsLoading(true);
          break;
        case 'ready':
          setIsLoading(false);
          setIsReady(true);
          break;
        case 'error':
          setError(data.error);
          if (onError) onError(new Error(data.error));
          break;
        default:
          break;
      }
    });

    return unsubscribe;
  }, [onError]);

  return {
    isLoading,
    isReady,
    isRunning,
    error,
    results,
    manualBoxes,
    initialize,
    start,
    stop,
    detectOnce,
    drawResults,
    updateConfig,
    setManualBox,
    clearManualBoxes,
    adjustBoxSize,
    moveBox,
    service: DetectionService
  };
}
